import { Link } from "react-router-dom";
import type { DirectoryPerson } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import {
  personCompanyLinkId,
  personOrganizationName,
} from "@/lib/person-company-link";

function locationOf(p: DirectoryPerson): string {
  return [p.city, p.state, p.country].filter(Boolean).join(", ") || "—";
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" }) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-brand/15 font-semibold text-brand",
        size === "sm" ? "size-6 text-[9px]" : "size-8 text-[10px]",
      )}
    >
      {initials(name)}
    </div>
  );
}

/** Stable row id when apolloPersonId is absent (directory-only contacts). */
export function personRowId(p: DirectoryPerson): string {
  return p.apolloPersonId ?? p.personId;
}

export function PeopleResultsTable({
  people,
  selectedIds,
  onToggle,
  onToggleAll,
  onOpen,
  companyDetailBase = "/companies",
}: {
  people: DirectoryPerson[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (all: boolean) => void;
  onOpen: (person: DirectoryPerson) => void;
  /** Where company name links go — directory vs user account. */
  companyDetailBase?: "/companies" | "/user/accounts";
  /** @deprecated Apollo unlock — unused for directory data */
  onEnrich?: (person: DirectoryPerson, kind: "email" | "phone") => void;
  enrichingId?: string | null;
}) {
  const allSelected =
    people.length > 0 && people.every((p) => selectedIds.has(personRowId(p)));

  if (people.length === 0) {
    return (
      <div className="border-t border-border px-4 py-16 text-center text-sm text-muted-foreground">
        No people yet. Adjust filters and search.
      </div>
    );
  }

  return (
    <div className="relative min-h-0 min-w-0 flex-1 overflow-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
      <table className="w-full min-w-[1200px] border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="bg-card text-left text-[10px] uppercase tracking-wide text-muted-foreground shadow-[inset_0_-1px_0_var(--border)]">
            <th className="w-9 bg-card px-3 py-2.5">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </th>
            <th className="bg-card px-3 py-2.5 font-semibold">Name</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Company</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Email</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Phone</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Location</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Seniority</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Stage</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Industry</th>
            <th className="bg-card px-3 py-2.5 font-semibold">Links</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => {
            const rowId = personRowId(p);
            const selected = selectedIds.has(rowId);
            const companyLinkId = personCompanyLinkId(p, companyDetailBase);
            const orgName = personOrganizationName(p);

            return (
              <tr
                key={rowId}
                className={cn(
                  "border-b border-border hover:bg-muted/10",
                  selected && "bg-brand-soft/40",
                  p.alreadySaved && !selected && "bg-muted/5",
                )}
              >
                <td className="border-b border-border px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggle(rowId)}
                  />
                </td>
                <td className="border-b border-border px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <Avatar name={p.name} />
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => onOpen(p)}
                        className="block truncate text-left text-[13px] font-semibold text-brand hover:underline"
                      >
                        {p.name}
                      </button>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {p.title ?? "—"}
                        {p.alreadySaved ? " · In leads" : ""}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="border-b border-border px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <Avatar name={orgName} size="sm" />
                    {companyLinkId ? (
                      <Link
                        to={`${companyDetailBase}/${companyLinkId}`}
                        className="truncate text-[13px] text-foreground hover:text-brand hover:underline"
                      >
                        {orgName}
                      </Link>
                    ) : (
                      <span className="truncate text-[13px] text-foreground">
                        {orgName}
                      </span>
                    )}
                  </div>
                </td>
                <td className="border-b border-border px-3 py-2.5">
                  {p.email ? (
                    <span className="truncate text-[12px]">{p.email}</span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">—</span>
                  )}
                </td>
                <td className="border-b border-border px-3 py-2.5">
                  {p.phone || p.mobilePhone ? (
                    <span className="truncate text-[12px]">
                      {p.phone || p.mobilePhone}
                    </span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">—</span>
                  )}
                </td>
                <td className="max-w-[120px] truncate border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {locationOf(p)}
                </td>
                <td className="border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {p.seniority ?? "—"}
                </td>
                <td className="border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {p.stage ?? "—"}
                </td>
                <td className="max-w-[110px] truncate border-b border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                  {p.organization?.industry ?? "—"}
                </td>
                <td className="border-b border-border px-3 py-2.5">
                  {p.linkedinUrl ? (
                    <a
                      href={p.linkedinUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] text-brand hover:underline"
                    >
                      LinkedIn
                    </a>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
